import { useCurrentUser } from 'hooks/useCurrentUser';
import { UPDATE_WORKLIST_SETTINGS, GET_ME } from 'modules/Apollo/queries';
import { useMutation } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import type { GetMeQuery, GetMeQueryVariables } from 'generated/graphql';
import { WorklistSurface } from '../types';
import { FilterStateType } from 'domains/worklist/WorklistFilters/types';
import { logger } from 'modules/logger';
import { hasSameItems, getWorklistKey } from 'domains/worklist/Worklist/utils/worklistUtils';
import { WithApolloContext } from 'modules/Apollo/types';

export type SaveSortingPreferencesArgs = {
  surface: string;
  columns: string;
  orders: string;
  filters: FilterStateType;
  worklistViewID?: string[];
};

type UseSavedSortingPreferencesReturn = {
  savedSortingPreferences: {
    [surface: string]: string;
  };
  saveSortingPreferences: (args: SaveSortingPreferencesArgs) => void;
};

export const useSavedSortingPreferences = ({
  context,
  skipQuery = false,
}: WithApolloContext & { skipQuery?: boolean }): UseSavedSortingPreferencesReturn => {
  const meQuery = useCurrentUser({ context, skip: skipQuery });
  const worklistSettings = meQuery?.data?.me.worklistSettings;
  const [updateWorklistSettings] = useMutation(UPDATE_WORKLIST_SETTINGS, {
    context,
  });

  const savedSortingPreferences = useMemo(() => {
    const preferences = worklistSettings?.savedSortingPreferences ?? [];

    return preferences.reduce<{
      [key: string]: string;
    }>(
      (
        acc: {
          [key: string]: string;
        },
        { surface, searchParams, worklistViewID }
      ) => {
        if (surface === WorklistSurface.CUSTOM_WORKLIST) {
          acc[`${surface}_${getWorklistKey(worklistViewID ?? [])}`] = searchParams;
        } else {
          acc[surface] = searchParams;
        }
        return acc;
      },
      {}
    );
  }, [worklistSettings?.savedSortingPreferences]);

  const saveSortingPreferences = useCallback(
    ({ surface, columns, orders, filters, worklistViewID = [] }: SaveSortingPreferencesArgs) => {
      const updatedSortingPreferences: Array<
        | any
        | {
            searchParams: string;
            surface: string;
          }
        | {
            searchParams: string;
            surface: string;
            worklistViewID: string[];
          }
      > = [];

      const filterParams = new URLSearchParams();
      const concatenatedWorklistViewIDs = getWorklistKey(worklistViewID);
      const surfaceWithViewID = `${surface}_${concatenatedWorklistViewIDs}`;

      // Append filters to the filterParams URLSearchParams
      Object.entries(filters).forEach(([key, values]) => {
        const valuesArray = Array.isArray(values) ? values : [values];
        if (valuesArray.length > 0) {
          filterParams.append(`filters[${key}]`, valuesArray.join(','));
        }
      });

      const filterString = filterParams.toString();
      // if there are no filters, we don't need to add the '&' at the end
      const filterStringWithAmpersand = filterString ? `${filterString}&` : '';

      const params = `${filterStringWithAmpersand}sortColumn=${columns}&sortOrder=${orders}&tab=${surface}`;

      const exists = worklistSettings?.savedSortingPreferences?.find((preference) => {
        if (surface === WorklistSurface.CUSTOM_WORKLIST) {
          return (
            preference.surface === surfaceWithViewID &&
            hasSameItems(preference.worklistViewID ?? [], worklistViewID ?? [])
          );
        } else {
          return preference.surface === surface;
        }
      });

      if (exists && exists.searchParams === params) {
        logger.info(
          `[useSavedSortingPreferences] Sorting preferences already exist for ${surface} with the same parameters`,
          {
            surface,
            params,
            worklistViewID,
          }
        );
        return;
      }

      const shouldSupplyWorklistView =
        worklistViewID?.length > 0 && surface === WorklistSurface.CUSTOM_WORKLIST;

      const sortingPreferencesKey = shouldSupplyWorklistView ? surfaceWithViewID : surface;

      if (!exists) {
        updatedSortingPreferences.push({
          surface: shouldSupplyWorklistView ? surfaceWithViewID : surface,
          searchParams: params,
          worklistViewID: shouldSupplyWorklistView ? worklistViewID : [],
        });
      }

      worklistSettings?.savedSortingPreferences?.forEach((preference: any) => {
        if (preference.surface !== sortingPreferencesKey) {
          updatedSortingPreferences.push({
            ...preference,
            worklistViewID: preference.worklistViewID ?? [],
          });
        } else {
          updatedSortingPreferences.push({
            surface: sortingPreferencesKey,
            searchParams: params,
            worklistViewID: shouldSupplyWorklistView ? worklistViewID : [],
          });
        }
      });

      const updatedResponse = {
        ...worklistSettings,
        savedSortingPreferences: updatedSortingPreferences,
      } as const;

      updateWorklistSettings({
        variables: { savedSortingPreferences: updatedSortingPreferences },
        optimisticResponse: {
          __typename: 'Mutation',
          updateWorklistSettings: {
            __typename: 'WorklistSettings',
            ...updatedResponse,
          },
        },
        update: (proxy) => {
          const { me } = proxy.readQuery<GetMeQuery, GetMeQueryVariables>({ query: GET_ME }) ?? {};

          proxy.writeQuery({
            query: GET_ME,
            data: {
              me: {
                ...me,
                worklistSettings: {
                  ...updatedResponse,
                },
              },
            },
          });
        },
        onCompleted: () => {
          logger.info(
            `[useSavedSortingPreferences] Sorting preferences have been saved for surface ${surface}${worklistViewID ? ` with worklistViewID ${worklistViewID}` : ''}`,
            {
              sortingPreferencesKey,
              surface,
              params,
              worklistViewID,
            }
          );
        },
      });
    },
    [updateWorklistSettings, worklistSettings]
  );

  return { savedSortingPreferences, saveSortingPreferences };
};
